import * as React from 'react';
import { Link } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { CONTENT_MAX_WIDTH } from '../../theme';

interface PageShellProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Buttons/controls aligned right of the title. */
  actions?: React.ReactNode;
  /** Small "← Label" link rendered above the title. */
  back?: { to: string; label: string };
  /** Rendered under the header, e.g. Tabs. */
  toolbar?: React.ReactNode;
  children?: React.ReactNode;
}

/** Standard authenticated page: header + constrained content. AppShell handles the AppBar/sidebar offsets. */
const PageShell = ({ title, subtitle, actions, back, toolbar, children }: PageShellProps) => (
  <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: { xs: 3, md: 4 }, pb: { xs: 4, md: 6 } }}>
    <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, mx: 'auto' }}>
      {back && (
        <Button
          component={Link}
          to={back.to}
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          sx={{ color: 'text.secondary', mb: 1, ml: -1, px: 1 }}
        >
          {back.label}
        </Button>
      )}
      {(title || actions) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-end' }, mb: toolbar ? 2 : 4 }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title && <Typography variant="h4" component="h1">{title}</Typography>}
            {subtitle && (
              <Typography variant="body1" component="div" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {actions && (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
              {actions}
            </Stack>
          )}
        </Stack>
      )}
      {toolbar && <Box sx={{ mb: 3 }}>{toolbar}</Box>}
      {children}
    </Box>
  </Box>
);

export default PageShell;
